import { ReactNode, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import { SignaturePad } from '@/components/SignaturePad/SignaturePad';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconUndo from '@/icons/Undo.svg';

export const SignatureDialog = ({
  isOpen,
  setIsOpen,
  title,
  submitLabel,
  onSubmit,
  isSubmitLoading,
  submitError,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: ReactNode;
  submitLabel?: ReactNode;
  onSubmit: (signature: string) => void;
  isSubmitLoading: boolean;
  submitError: string;
}) => {
  const [signature, setSignature] = useState<string | null>(null);
  const [resetNumber, setResetNumber] = useState(0);

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen && isSubmitLoading) {
          // Do not close the dialog if the submit action is in progress
          return;
        }

        setIsOpen(newIsOpen);
      }}
    >
      <main>
        <Stack gap="0.5rem">
          <Paragraph color="secondary">
            <Trans id="requests.signature-dialog.description">
              Ask the client to add their signature in the rectangle below:
            </Trans>
          </Paragraph>
          <div
            style={{
              width: '100%',
              height: '200px',
              border: '2px solid var(--color-text-primary-black)',
              borderRadius: '4px',
            }}
          >
            <SignaturePad key={resetNumber} onSignatureChange={setSignature} />
          </div>
        </Stack>
      </main>
      <Footer>
        {!!submitError && <FooterError>{submitError}</FooterError>}
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={() => {
              setSignature(null);
              setResetNumber((prev) => prev + 1);
            }}
            disabled={!signature || isSubmitLoading}
          >
            <IconUndo />
            <Trans id="requests.signature-dialog.reset">Reset</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={() => {
              if (signature) {
                onSubmit(signature);
              }
            }}
            isLoading={isSubmitLoading}
          >
            {submitLabel ?? <Trans id="requests.signature-dialog.submit">Save</Trans>}
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
